import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

export const LoginPage = () => {
  const { t, login, language } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      toast.success(language === 'ro' ? 'Autentificare reușită!' : 'Login successful!');
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err.message || (language === 'ro' ? 'Autentificare eșuată' : 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-16" data-testid="login-page">
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl font-bold text-[#1A1625] mb-2">
            {t('login')}
          </h1>
          <p className="text-muted-foreground">
            {language === 'ro' ? 'Bine ai revenit la Rani Store' : 'Welcome back to Rani Store'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="email">{t('email')}</Label>
            <Input
              id="email"
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              data-testid="login-email"
            />
          </div>
          <div>
            <Label htmlFor="password">{language === 'ro' ? 'Parolă' : 'Password'}</Label>
            <Input
              id="password"
              type="password"
              required
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              data-testid="login-password"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-primary hover:bg-purple-600 text-white rounded-full"
            data-testid="login-submit"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t('login')}
          </Button>
        </form>

        <p className="text-center text-sm text-muted-foreground mt-6">
          {language === 'ro' ? 'Nu ai cont?' : "Don't have an account?"}{' '}
          <Link to="/register" className="text-primary hover:underline">
            {t('register')}
          </Link>
        </p>
      </div>
    </Layout>
  );
};

export const RegisterPage = () => {
  const { t, register, language } = useApp();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error(language === 'ro' ? 'Parolele nu coincid' : 'Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await register({
        email: formData.email,
        password: formData.password,
        first_name: formData.firstName,
        last_name: formData.lastName,
      });
      toast.success(language === 'ro' ? 'Înregistrare reușită!' : 'Registration successful!');
      navigate('/');
    } catch (err) {
      toast.error(err.message || (language === 'ro' ? 'Înregistrare eșuată' : 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-16" data-testid="register-page">
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl font-bold text-[#1A1625] mb-2">
            {t('register')}
          </h1>
          <p className="text-muted-foreground">
            {language === 'ro' ? 'Creează-ți contul Rani Store' : 'Create your Rani Store account'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName">{t('firstName')}</Label>
              <Input
                id="firstName"
                required
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                data-testid="register-firstname"
              />
            </div>
            <div>
              <Label htmlFor="lastName">{t('lastName')}</Label>
              <Input
                id="lastName"
                required
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                data-testid="register-lastname"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="email">{t('email')}</Label>
            <Input
              id="email"
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              data-testid="register-email"
            />
          </div>
          <div>
            <Label htmlFor="password">{language === 'ro' ? 'Parolă' : 'Password'}</Label>
            <Input
              id="password"
              type="password"
              required
              minLength={6}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              data-testid="register-password"
            />
          </div>
          <div>
            <Label htmlFor="confirmPassword">{language === 'ro' ? 'Confirmă parola' : 'Confirm Password'}</Label>
            <Input
              id="confirmPassword"
              type="password"
              required
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              data-testid="register-confirm-password"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-primary hover:bg-purple-600 text-white rounded-full"
            data-testid="register-submit"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t('register')}
          </Button>
        </form>

        <p className="text-center text-sm text-muted-foreground mt-6">
          {language === 'ro' ? 'Ai deja un cont?' : 'Already have an account?'}{' '}
          <Link to="/login" className="text-primary hover:underline">
            {t('login')}
          </Link>
        </p>
      </div>
    </Layout>
  );
};

export default LoginPage;
